import Button from "../../../Buttons/Button";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Column, Label, Line } from "../../../../style";
import { secondary } from "../../../../theme";
import { useApi } from "../../../../hooks/api";
import { useGlobal } from "../../../../hooks/global";
import { ModalLoading } from "../../../Loadings/ModalLoading";
import { ContainerButtons, ContainerForm, ContainerTextArea, Form, TextArea, VariableButton } from "./style";
import './style.css';
import { FixRequiredSelect } from "../../../Inputs/BaseSelects/FixRequiredSelect";
import { html } from 'js-beautify';
import Input from "../../../Inputs/Input";
import { Editor } from '@tinymce/tinymce-react';
import { AppConstants } from "../../../../constants";
import { ICONS_MAPPER, VARIABLES } from "../../../../utils/variables";
import { Controller, useForm } from "react-hook-form";

interface IFormContractModelModalProps {
  onCancel: Function;
  onSave: Function;
  accommodationOptions: { label: string; value: string }[];
  groupOptions: { label: string; value: string }[];
  defaultData?: {
    id: string;
    title: string;
    type: string;
    text: string;
    name: string;
    group_id: string | { value: string; label: string };
    accommodation_ids: string[] | { value: string; label: string }[];
  } | null;
}

const visualizations_modes_options = [
  { label: 'HTML', value: 'html' },
  { label: 'Editor de texto', value: 'text' }
]

function formatHTML(htmlString: string): string {
  return html(htmlString, { indent_size: 4, space_in_empty_paren: true });
}

export const FormContractModel: React.FC<IFormContractModelModalProps> = (props) => {
  const { onSave, defaultData, accommodationOptions, groupOptions } = props;

  const { theme, notify, notifyBottom } = useGlobal();
  const { api } = useApi();

  const [visualizationMode, setVisualizationMode] = useState('text');
  const [type, setType] = useState(defaultData?.type ?? 'email');
  const [_firstRender, setFirstRender] = useState(true);
  const [loadingCreate, setLoadingCreate] = useState(false);
  const [message, setMessage] = useState(defaultData?.text ?? "");
  const [messageError, setMessageError] = useState<string | null>(null);

  const submitButtonRef = useRef<HTMLButtonElement>(null);

  const {
    handleSubmit,
    setError,
    control,
    getValues,
    setValue
  } = useForm<{
    name: string;
    title: string;
    group_id: string | null;
    accommodation_ids: string[];
  }>({
    defaultValues: {
      name: defaultData?.name ?? '',
      title: defaultData?.title ?? '',
      group_id: null,
      accommodation_ids: [],
    }
  });

  const onSubmit = useCallback(async () => {
    const { name, title, group_id, accommodation_ids } = getValues();
    
    if (!name || name.trim() === '') {
      setError('name', { type: 'manual', message: 'Nome é obrigatório' });
      return;
    }
    
    if (!title || title.trim() === '') {
      setError('title', { type: 'manual', message: 'Título é obrigatório' });
      return;
    }
    
    if (!message || message.trim() === '') {
      setMessageError('Mensagem é obrigatória');
      return;
    }
    
    setMessageError(null);
    
    if ((!accommodation_ids || accommodation_ids.length === 0) && !group_id) {
      notify('É necessário selecionar uma acomodação ou um grupo', 'error');
      return;
    }

    setLoadingCreate(true);

    try {
      const form_data = {
        name: name.trim(),
        title: title.trim(),
        group_id,
        accommodation_ids,
        text: message.trim(),
      };

      if (defaultData?.id) {
        await api.put(`/contract-models/${defaultData?.id}`, form_data);
      } else {
        await api.post("/contract-models", form_data);
      }

      notify(`Modelo de mensagem ${defaultData?.id ? "atualizado" : "criado"} com sucesso!`, "success");

      onSave();
    } catch (err: any) {
      const message = err?.response?.data?.detail ?? "Erro ao criar o modelo de mensagem!";

      if (message === "Nome já em uso") {
        setError('name', { type: 'manual', message: 'Nome já em uso' });
      } else {
        notify(message, "error");
      }
    } finally {
      setLoadingCreate(false);
    }

  }, [message, api, getValues, setError, notify, onSave, defaultData?.id]);

  const handleChange = (value: string) => {
    setMessage(value);
    if (messageError && value.trim() !== '') {
      setMessageError(null);
    }
  };

  const copyText = (textToCopy: string) => {
    navigator.clipboard.writeText(textToCopy)
      .then(() => {
        notifyBottom("Variável copiada para área de tranferência!", "success");
      })
      .catch((err) => {
        notifyBottom("Erro ao copiar a variável para a área de tranferência!", "error");
      });
  };

  const handleKeyDownInTextArea = (e: any) => {
    if (e.key === 'Tab') {
      e.preventDefault();

      const textarea = e?.target;
      const start = textarea?.selectionStart;
      const end = textarea?.selectionEnd;

      textarea.value = textarea?.value?.substring(0, start) + '\t' + textarea?.value?.substring(end);

      textarea.selectionStart = textarea.selectionEnd = start + 4;
    }
  }

  useEffect(() => {
    setFirstRender((prev) => {
      if (prev) return false

      setMessage('')

      if (type === 'wpp') {
        setVisualizationMode('text')
      }

      return true
    })
  }, [type])

  useEffect(() => {
    if (visualizationMode === 'html') setMessage((prev) => formatHTML(prev ?? ''))
  }, [visualizationMode])

  useEffect(() => {
    if (defaultData?.accommodation_ids) {
      let values: string[] = [];
      if (Array.isArray(defaultData.accommodation_ids)) {
        values = defaultData.accommodation_ids.map((item: any) => typeof item === 'object' ? item.value : item);
      }
      setValue('accommodation_ids', values);
    }

    if (defaultData?.group_id) {
      const value = typeof defaultData.group_id === 'object' ? defaultData.group_id.value : defaultData.group_id;
      setValue('group_id', value);
    }
  }, [defaultData, setValue]);

  return (
    <>
      <Column style={{ flex: 1, overflow: "auto", flexDirection: "row", gap: 16, padding: 16, flexWrap: 'wrap' }}>
        <ContainerForm open style={{ flex: 4, paddingBottom: 0, minWidth: 300 }}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Column style={{ gap: 16 }}>
              <Column style={{ gap: 16 }}>

                <Line style={{ gap: 16, flexWrap: 'wrap' }}>
                  <Column style={{ flex: 1 }}>
                    <Label>Nome*</Label>
                    <Controller
                      name="name"
                      control={control}
                      rules={{ required: 'Nome é obrigatório' }}
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          error={fieldState.error?.message || null}
                        />
                      )}
                    />
                  </Column>

                  <Column style={{ flex: 1 }}>
                    <Label>Modo de visualização*</Label>
                    <FixRequiredSelect
                      placeholder=""
                      options={visualizations_modes_options}
                      value={visualizations_modes_options.find((item) => item.value === visualizationMode)}
                      onChange={(value: any) => setVisualizationMode(value?.value)}
                    />
                  </Column>
                </Line>


                <Line style={{ gap: 16, flexWrap: 'wrap' }}>
                  <Column style={{ flex: 1 }}>
                    <Label>Acomodação*</Label>
                    <Controller
                      control={control}
                      name="accommodation_ids"
                      render={({ field }) => {
                        const groupSelected = !!getValues('group_id');
                        if (groupSelected && field.value && field.value.length > 0) {
                          setValue('accommodation_ids', []);
                          field.onChange([]);
                        }
                        return (
                          <FixRequiredSelect
                            {...field}
                            isMulti
                            options={accommodationOptions}
                            placeholder=""
                            required={false}
                            defaultValue={
                              defaultData && Array.isArray(defaultData.accommodation_ids)
                                ? defaultData.accommodation_ids.map((item: any) =>
                                    typeof item === 'object'
                                      ? accommodationOptions.find(opt => opt.value === item.value)
                                      : accommodationOptions.find(opt => opt.value === item)
                                  )
                                : []
                            }
                            value={groupSelected ? [] : accommodationOptions.filter(item => field.value?.includes(item.value))}
                            onChange={(vals: any) => {
                              const selected = Array.isArray(vals) ? vals.map((v: any) => v.value) : [];
                              field.onChange(selected);
                              if (selected.length > 0) {
                                setValue('group_id', null);
                              }
                            }}
                          />
                        )
                      }}
                    />
                  </Column>
                </Line>

                <Line style={{ gap: 16, flexWrap: 'wrap' }}>
                  <Column style={{ flex: 1 }}>
                    <Label>Grupo*</Label>
                    <Controller
                      control={control}
                      name="group_id"
                      render={({ field }) => {
                        const accommodationSelected = getValues('accommodation_ids')?.length > 0;
                        if (accommodationSelected && field.value !== null) {
                          setValue('group_id', null);
                          field.onChange(null);
                        }
                        return (
                          <FixRequiredSelect
                            {...field}
                            options={groupOptions}
                            disabled={accommodationSelected}
                            required={false}
                            defaultValue={defaultData?.group_id}
                            placeholder=""
                            value={accommodationSelected ? null : groupOptions.find(item => item.value === field.value)}
                            onChange={(val: any) => {
                              field.onChange(val?.value);
                              if (val?.value) {
                                setValue('accommodation_ids', []);
                              }
                            }}
                          />
                        )
                      }}
                    />
                  </Column>
                </Line>

                <Line style={{ gap: 16 }}>
                  <Column style={{ flex: 1 }}>
                    <Label>Título*</Label>
                    <Controller
                      name="title"
                      control={control}
                      rules={{ required: 'Título é obrigatório' }}
                      render={({ field, fieldState }) => (
                        <Input
                          {...field}
                          error={fieldState.error?.message || null}
                        />
                      )}
                    />

                  </Column>
                </Line>

              </Column>
              <Column style={{ gap: 5 }}>
                <Label>Contrato*</Label>
                {messageError && (
                  <div style={{ color: '#ff4444', fontSize: 12, marginBottom: 5 }}>
                    {messageError}
                  </div>
                )}
                {visualizationMode === 'html' && (
                  <TextArea
                    theme={theme}
                    value={message}
                    onKeyDown={handleKeyDownInTextArea}
                    onChange={(e) => {
                      setMessage(e?.target?.value);
                      if (messageError && e?.target?.value.trim() !== '') {
                        setMessageError(null);
                      }
                    }}
                    placeholder="Edite seu html manualmente aqui..."
                  />
                )}
                {visualizationMode === 'text' && (
                  <ContainerTextArea theme={theme}>
                    {type === 'email' && (
                      <Editor
                        apiKey={AppConstants.TINY_API_KEY}
                        value={message}
                        init={{
                          height: 'calc(100vh - 403px)',
                          language: 'pt_BR',
                          menubar: false,
                          plugins: 'advlist autolink lists link image charmap print preview anchor searchreplace visualblocks code fullscreen insertdatetime media table paste code help wordcount',
                          toolbar: 'fontsize fontfamily | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist table | link image fullscreen ',
                          resize: false,
                        }}
                        onEditorChange={handleChange}
                      />
                    )}
                    {type === 'wpp' && (
                      <Editor
                        apiKey={AppConstants.TINY_API_KEY}
                        value={message}
                        init={{
                          height: 'calc(100vh - 325px)',
                          language: 'pt_BR',
                          menubar: false,
                          end_container_on_empty_block: true,
                          plugins: "fullscreen paste",
                          toolbar: 'bold italic strikethrough | fullscreen',
                          resize: false,
                        }}
                        onEditorChange={handleChange}
                      />
                    )}
                  </ContainerTextArea>
                )}
              </Column>
            </Column>
            <button
              className="display-none"
              type="submit"
              ref={submitButtonRef}
            />
          </Form>
        </ContainerForm>
        <div style={{ display: "flex", flex: 1, justifyContent: "center" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 300, flex: 1 }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ fontWeight: "bold" }}>Variáveis</div>
              <div style={{ fontSize: 14 }}>
                Clique em uma variável para copiar a chave correspondente.
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 10, overflow: "auto", marginLeft: -10 }}>
              {VARIABLES.map((v) => (
                <VariableButton onClick={() => copyText(`{{${v.value}}}`)} key={v.value} theme={theme}>
                  {ICONS_MAPPER[v.type]} {v.label}
                </VariableButton>
              ))}
            </div>
          </div>
        </div>
      </Column>
      <ContainerButtons theme={theme}>
        <Button
          onClick={handleSubmit(onSubmit)}
          background={secondary[theme]}
          color={"#FFF"}
          style={{ padding: 8, maxWidth: 150, fontSize: 14 }}
          children={"Salvar"}
        />
      </ContainerButtons>

      <ModalLoading loading={loadingCreate} theme={theme} />
    </>
  );
};
